import {
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Typography,
} from "@mui/material";
import { Form, Formik, FormikProps } from "formik";
import { useState } from "react";
import * as Yup from "yup";
import AppSelect from "../../components/AppSelect";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import ErrorText from "../../components/ErrorText";
import LoadingModal from "../../components/LoadingModal";
import AppointmentApplicationType from "../../models/AppointmentApplicationType";
import AppointmentApplicationTime from "../../models/AppointmentApplicationTime";
import accountService from "../../services/AccountService";
import utilsService from "../../services/UtilsService";
import { GetDateCompactYYYYMMDD, GetDateDDMMYYYYY } from "../../Utils";
import AccountAppointment from "../../models/AccountAppointment";

interface OnsiteAppointmentModalProps {
  onClose: () => void;
  onSuccess?: (results: AccountAppointment[]) => void;
  appointmentTypes: AppointmentApplicationType[];
}

interface IOnsiteAppointmentForm {
  applicationTypeCode: string;
  appointmentDate: string;
  appointmentTime: string;
}

const validationSchema = Yup.object().shape({
  applicationTypeCode: Yup.string().required("Requerido"),
  appointmentDate: Yup.string().required("Requerido"),
  appointmentTime: Yup.string().required("Requerido"),
});

function OnsiteAppointmentModal({
  onClose,
  onSuccess,
  appointmentTypes,
}: OnsiteAppointmentModalProps) {
  const [appointmentDates, setAppointmentDates] = useState<string[]>([]);
  const [appointmentTimes, setAppointmentTimes] = useState<
    AppointmentApplicationTime[]
  >([]);
  const [isLoadingDates, setIsLoadingDates] = useState(false);
  const [isLoadingTimes, setIsLoadingTimes] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogDesc, setDialogDesc] = useState("");
  const [dialogButton, setDialogButton] = useState<
    JSX.Element | JSX.Element[] | undefined
  >();

  const initialValues: IOnsiteAppointmentForm = {
    applicationTypeCode: "",
    appointmentDate: "",
    appointmentTime: "",
  };

  async function submit(values: IOnsiteAppointmentForm) {
    try {
      setIsSaving(true);
      const results = await accountService.requestAppointment({
        applicationTypeCode: values.applicationTypeCode,
        appoinmentDateTime: values.appointmentTime,
      });
      setIsSaving(false);
      setOpenDialog(true);
      setDialogTitle("Gracias");
      setDialogDesc("Su solicitud de cita ha sido enviada correctamente.");
      onSuccess?.(results);
      setDialogButton(
        <Button
          onClick={() => {
            setOpenDialog(false);
            onClose();
          }}
        >
          Ok
        </Button>,
      );
    } catch {
      setIsSaving(false);
      setOpenDialog(true);
      setDialogTitle("Error");
      setDialogDesc("Ha ocurrido un error. Por favor intente mas tarde");
      setDialogButton(<Button onClick={() => setOpenDialog(false)}>Ok</Button>);
    }
  }

  return (
    <Box>
      <Formik
        initialValues={initialValues}
        onSubmit={submit}
        validationSchema={validationSchema}
        validateOnMount={true}
      >
        {(props: FormikProps<IOnsiteAppointmentForm>) => {
          const { values, setFieldValue, touched, errors, isSubmitting } =
            props;

          return (
            <Form>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="body1" style={{ marginBottom: "5px" }}>
                    Tipo de Cita
                  </Typography>
                  <AppSelect
                    name="applicationTypeCode"
                    error={
                      errors.applicationTypeCode && touched.applicationTypeCode
                        ? true
                        : false
                    }
                    fullWidth
                    value={values.applicationTypeCode}
                    onChange={async (event) => {
                      const typeCode = event.target.value as string;
                      setFieldValue("applicationTypeCode", typeCode);
                      setFieldValue("appointmentDate", "");
                      setFieldValue("appointmentTime", "");
                      setAppointmentDates([]);
                      setAppointmentTimes([]);

                      if (!typeCode) return;

                      setIsLoadingDates(true);
                      try {
                        const dates =
                          await utilsService.getAppointmentApplicationDates(
                            typeCode,
                          );
                        setAppointmentDates(dates);
                      } finally {
                        setIsLoadingDates(false);
                      }
                    }}
                  >
                    {appointmentTypes.map((type) => (
                      <MenuItem
                        key={type.solicitudCitaTipoCodigo}
                        value={type.solicitudCitaTipoCodigo}
                      >
                        {type.solicitudCitaTipoDescripcion}
                      </MenuItem>
                    ))}
                  </AppSelect>
                  {errors.applicationTypeCode && touched.applicationTypeCode ? (
                    <ErrorText text={errors.applicationTypeCode} />
                  ) : null}
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body1" style={{ marginBottom: "5px" }}>
                    Fecha
                  </Typography>
                  <Box sx={{ position: "relative" }}>
                    <AppSelect
                      name="appointmentDate"
                      error={
                        errors.appointmentDate && touched.appointmentDate
                          ? true
                          : false
                      }
                      fullWidth
                      disabled={
                        !values.applicationTypeCode ||
                        isLoadingDates ||
                        appointmentDates.length === 0
                      }
                      value={values.appointmentDate}
                      onChange={async (event) => {
                        const dateValue = event.target.value as string;
                        setFieldValue("appointmentDate", dateValue);
                        setFieldValue("appointmentTime", "");
                        setAppointmentTimes([]);

                        if (!dateValue || !values.applicationTypeCode) return;

                        setIsLoadingTimes(true);
                        try {
                          const times =
                            await utilsService.getAppointmentApplicationTimes(
                              values.applicationTypeCode,
                              GetDateCompactYYYYMMDD(new Date(dateValue)),
                            );
                          setAppointmentTimes(times);
                        } finally {
                          setIsLoadingTimes(false);
                        }
                      }}
                    >
                      {appointmentDates.map((date) => (
                        <MenuItem key={date} value={date}>
                          {GetDateDDMMYYYYY(new Date(date))}
                        </MenuItem>
                      ))}
                    </AppSelect>
                    {isLoadingDates && (
                      <CircularProgress
                        color="secondary"
                        size={20}
                        sx={{
                          position: "absolute",
                          right: 36,
                          top: "50%",
                          marginTop: "-10px",
                        }}
                      />
                    )}
                  </Box>
                  {errors.appointmentDate && touched.appointmentDate ? (
                    <ErrorText text={errors.appointmentDate} />
                  ) : null}
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body1" style={{ marginBottom: "5px" }}>
                    Hora
                  </Typography>
                  <Box sx={{ position: "relative" }}>
                    <AppSelect
                      name="appointmentTime"
                      error={
                        errors.appointmentTime && touched.appointmentTime
                          ? true
                          : false
                      }
                      fullWidth
                      disabled={
                        !values.appointmentDate ||
                        isLoadingTimes ||
                        appointmentTimes.length === 0
                      }
                      value={values.appointmentTime}
                      onChange={(event) => {
                        setFieldValue(
                          "appointmentTime",
                          event.target.value as string,
                        );
                      }}
                    >
                      {appointmentTimes.map((time) => (
                        <MenuItem
                          key={time.appointmentDateTime}
                          value={time.appointmentDateTime}
                        >
                          {time.fechaHoraDisplay.split(" ").slice(1).join(" ")}
                        </MenuItem>
                      ))}
                    </AppSelect>
                    {isLoadingTimes && (
                      <CircularProgress
                        color="secondary"
                        size={20}
                        sx={{
                          position: "absolute",
                          right: 36,
                          top: "50%",
                          marginTop: "-10px",
                        }}
                      />
                    )}
                  </Box>
                  {errors.appointmentTime && touched.appointmentTime ? (
                    <ErrorText text={errors.appointmentTime} />
                  ) : null}
                </Grid>

                <Grid item xs={12}>
                  <Button
                    onClick={onClose}
                    color="customGrey"
                    variant="contained"
                    sx={{ mr: "10px", color: "white" }}
                  >
                    Cerrar
                  </Button>
                  <Button
                    disabled={isSubmitting}
                    type="submit"
                    color="secondary"
                    variant="contained"
                    sx={{ bgcolor: "secondary.main" }}
                  >
                    {isSubmitting ? <>Guardando...</> : "Solicitar Cita"}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
      <ConfirmationDialog
        maxWidth="sm"
        open={openDialog}
        title={dialogTitle}
        description={dialogDesc}
        buttons={dialogButton}
      />
      <LoadingModal isLoading={isSaving} loadingMessage="Guardando" />
    </Box>
  );
}

export default OnsiteAppointmentModal;
